import {
  putBlindCommentAPI,
  putBlindPostingAPI,
} from '@/src/apis/rest/communityBlind';
import { PutBlindApiRequestBody } from '@/src/apis/rest/communityBlind.dtos';
import Button from '@/src/components/atom/Button';
import Dialog, { DialogProps } from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import TextArea from '@/src/components/atom/TextArea';
import FormContent from '@/src/components/molecule/FormContent';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { toast } from '@/src/hooks/useToast';
import useAuthStore from '@/src/store/useAuthStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface Props extends DialogProps {
  id?: number;
  blindType: 'posting' | 'comment';
  onSubmit: () => void;
}

export const formSchema = z.object({
  id: z.number(),
  communityReportType: z.string(),
  reportHandlingReason: z.string().min(1),
});

const BlindModal = ({ id, blindType, onSubmit, ...rest }: Props) => {
  const { auth } = useAuthStore();

  const {
    Dialog: BlindConfirm,
    isOpen,
    openDialog,
    closeDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const blindPostingMutation = useMutation({ mutationFn: putBlindPostingAPI });
  const blindCommentMutation = useMutation({ mutationFn: putBlindCommentAPI });

  const form = useForm<PutBlindApiRequestBody>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id,
      communityReportType: 'blind',
      reportHandlingReason: '',
    },
  });

  const handleCloseModal = () => {
    rest?.onOpenChange(false);
  };
  const handleSubmitRequest = () => {
    const values = form.watch();

    if (blindType === 'posting') {
      blindPostingMutation.mutate(
        {
          ...values,
        },
        {
          onSuccess: () => {
            handleCloseModal();
            onSubmit();
          },
          onError: e => {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          },
        },
      );
    } else {
      blindCommentMutation.mutate(
        {
          ...values,
          id,
        },
        {
          onSuccess: () => {
            handleCloseModal();
            onSubmit();
          },
          onError: e => {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          },
        },
      );
    }
  };
  const handleSubmit = () => {
    openDialog();
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          className="flex-1"
          buttonColor="outline"
          onClick={() => handleCloseModal()}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={!form.formState.isValid}
          isLoading={
            blindPostingMutation.isPending || blindCommentMutation.isPending
          }
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  useEffect(() => {
    return () => {
      if (!rest.open) {
        form.reset({
          id,
          communityReportType: 'blind',
          reportHandlingReason: '',
        });
      }
    };
  }, [form, id, rest.open]);

  return (
    <>
      <Dialog
        {...rest}
        title="블라인드 처리"
        contentProps={{
          className: 'max-w-[697px]',
        }}
      >
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="reportHandlingReason"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent
                    className="w-full flex-col [&>div]:w-full"
                    label="사유 입력"
                    labelClassName="!text-b16m text-gray7 translate-y-0 font-pretendardMedium"
                  >
                    <TextArea
                      placeholder="블라인드 사유를 입력해주세요"
                      className="h-[220px] w-full"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <SectionLabel
              direction="vertical"
              label="담당 관리자"
              value={
                <Input
                  containerClassName="w-full"
                  disabled
                  value={auth?.name || '-'}
                />
              }
            />
            {renderFooter()}
          </form>
        </Form>
      </Dialog>
      <BlindConfirm
        title="블라인드 처리 확인"
        open={isOpen}
        onOpenChange={closeDialog}
        onOk={handleSubmitRequest}
      >
        해당 {blindType === 'posting' ? '게시글' : '댓글'} 블라인드 처리를
        하시겠습니까?
        <br />
        블라인드 처리 이후 게시가 불가능합니다.
      </BlindConfirm>
    </>
  );
};

export default BlindModal;
